import type { Session } from '@ctcells/contract'

/**
 * 세션 목록 → **기기별 묶음**. 「내 계정」 로그인 정보 표의 한 줄은 세션이 아니라 기기다
 * (eric 2026-09-22 「브라우저 접속 시간이 아니라 기기별로 나와야 하는 거 아니야?」).
 *
 * 🔴 **종료 단추는 없다**(eric 2026-09-22 「기기 구분을 못하는데 강제 종료 · 다른 세션 모두 종료 버튼 빼고 로그인 정보로」).
 *    `client` 표지는 「같은 브라우저 프로필」까지만 말하고 누구 손에 있는 기기인지는 모르므로, 사람이 고른 줄을 끊는
 *    단추는 잘못 끊을 수 있다. 이 표는 **읽는 정보**(로그인 · 마지막 활동)만 준다. 강제 종료는 관리자의 사용자 상세에
 *    (세션 한 건씩 · 감사 기록과 함께) 남아 있다.
 *
 * 🔴 **기기를 가르는 축은 서버가 준 `client` 문자열 하나다.** 로그인 때 프론트가 브라우저 표지
 *    (`web/chrome-linux/<표지>` · `@ctcells/core` `clientIdentity()`)를 실어 보내므로 같은 브라우저의 세션은 같은 값을
 *    단다. 서버에 기기 축이 따로 없으니 이것이 유일한 재료다. `client` 가 없는 세션(옛 응답 · 다른 클라이언트)은
 *    「기기 미상」 한 묶음이다 — 지어내지 않는다.
 * 🔴 **살아 있는 세션만 센다** — `revoked` 도, `expires_at` 이 지난 것도 뺀다(`now` 를 넘겼을 때). 만료된 줄이 남아
 *    「많아 보이는」 것이 바로 eric 이 본 것이다. `now` 를 안 넘기면(demo · 정적 그리기) 만료 판정을 하지 않는다.
 * 🔴 **묶음의 만료 시각은 들고 있지 않다**(eric 2026-09-23 「절대 만료 이거 없애」 — 열이 없어졌다).
 *    `expires_at` 은 위 **거르기**에만 쓴다. 표시할 곳이 없는 값을 묶음에 얹어 두면 다음 사람이
 *    「응답에 있는 값」으로 알고 열을 다시 깐다.
 */
export interface DeviceSessions {
  /** 묶음 키 = `client` 원값. 없으면 `''` */
  client: string
  sessions: Session[]
  /** 이 묶음에 지금 쓰는 세션이 있는가 → 「이 기기」 */
  current: boolean
  /** 묶음 안 가장 최근 `last_seen_at` */
  lastSeenAt: string | undefined
  /** 묶음 안 가장 최근 로그인(`issued_at`) */
  issuedAt: string | undefined
}

export function isLiveSession(session: Session, now: number | undefined): boolean {
  if (session.revoked) return false
  if (now === undefined) return true
  const expires = Date.parse(session.expires_at)
  return Number.isNaN(expires) || expires > now
}

export function groupSessionsByDevice(
  sessions: readonly Session[],
  currentSessionId: string | undefined,
  now?: number,
): DeviceSessions[] {
  const groups = new Map<string, DeviceSessions>()
  for (const session of sessions) {
    if (!isLiveSession(session, now)) continue
    const key = session.client ?? ''
    const group: DeviceSessions = groups.get(key) ?? {
      client: key,
      sessions: [],
      current: false,
      lastSeenAt: undefined,
      issuedAt: undefined,
    }
    group.sessions.push(session)
    if (session.session_id === currentSessionId) group.current = true
    if (group.lastSeenAt === undefined || session.last_seen_at > group.lastSeenAt) {
      group.lastSeenAt = session.last_seen_at
    }
    if (group.issuedAt === undefined || session.issued_at > group.issuedAt) {
      group.issuedAt = session.issued_at
    }
    groups.set(key, group)
  }
  // 「이 기기」가 맨 위, 그 다음은 최근 본 순서
  return [...groups.values()].sort((a, b) => {
    if (a.current !== b.current) return a.current ? -1 : 1
    return (b.lastSeenAt ?? '').localeCompare(a.lastSeenAt ?? '')
  })
}
